import React, { useState } from "react";
import { Mail, Phone, MapPin, MessageSquare, Send, Loader2, CheckCircle, Briefcase, Headphones, Handshake, Newspaper, HelpCircle } from "lucide-react";
import { Button } from "../components/ui/button";
import PublicLayout from "../layouts/PublicLayout";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { key: "Sales", label: "Sales", icon: Briefcase, color: "#3B82F6", desc: "Pricing, certification packages, demo requests" },
  { key: "Support", label: "Support", icon: Headphones, color: "#10B981", desc: "Account, platform, assessment help" },
  { key: "Partnership", label: "Partnership", icon: Handshake, color: "#FDB813", desc: "Become a partner, referral programs" },
  { key: "Press", label: "Press & Media", icon: Newspaper, color: "#8B5CF6", desc: "Media enquiries, interviews, speaking" },
  { key: "General", label: "General", icon: HelpCircle, color: "#64748B", desc: "Everything else" },
];

const ContactPage = () => {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    organization: "",
    category: "Sales",
    subject: "",
    message: "",
  });

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/contact-submissions`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || "Submission failed");
      }
      setSubmitted(true);
    } catch (err) {
      setError(err.message || "Something went wrong.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PublicLayout>
      {/* Hero */}
      <section className="py-16 bg-gradient-to-br from-[#F8FAFC] to-white" data-testid="contact-hero">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#FEF3C7] rounded-full mb-6">
              <MessageSquare className="w-4 h-4 text-[#FDB813]" />
              <span className="text-sm font-medium text-[#1E3A5F]">Get in Touch</span>
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold text-[#1E3A5F] mb-6">
              Let's Start a <span className="text-[#FDB813]">Conscious Conversation</span>
            </h1>
            <p className="text-lg text-gray-600">
              Whether you're exploring certification, partnerships, or simply curious — we'd love to hear from you.
            </p>
          </div>
        </div>
      </section>

      {/* Contact info strip */}
      <section className="py-10 border-b border-gray-100">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            <InfoCard icon={Mail} title="Email" value="inquiries@cness.co" href="mailto:inquiries@cness.co" />
            <InfoCard icon={Phone} title="USA" value="+1 (562) 643-5001" href="tel:+15626435001" />
            <InfoCard icon={Phone} title="India" value="+91 1800 410 0108" href="tel:+918004100108" />
            <InfoCard icon={MapPin} title="Headquarters" value="Santa Monica, CA" sub="825 Wilshire Blvd #333, CA 90401, USA" />
          </div>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            {submitted ? (
              <div
                className="bg-gradient-to-br from-[#FEF3C7] to-white border border-[#FDB813]/30 rounded-3xl p-12 text-center"
                data-testid="contact-success-card"
              >
                <div className="w-16 h-16 bg-[#FDB813] rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="w-9 h-9 text-white" />
                </div>
                <h2 className="text-3xl font-bold text-[#1E3A5F] mb-4">Message received</h2>
                <p className="text-lg text-gray-700">
                  Thanks for reaching out, <strong>{form.full_name.split(" ")[0]}</strong>. We'll get back to you shortly.
                </p>
              </div>
            ) : (
              <>
                {/* Category Selector */}
                <h3 className="text-sm font-semibold text-[#1E3A5F] mb-4 text-center uppercase tracking-wider">
                  How can we help?
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-10">
                  {CATEGORIES.map((c) => {
                    const active = form.category === c.key;
                    return (
                      <button
                        key={c.key}
                        type="button"
                        onClick={() => update("category", c.key)}
                        data-testid={`contact-category-${c.key.toLowerCase()}`}
                        className={`p-4 rounded-xl border-2 transition-all text-left ${
                          active
                            ? "border-[#1E3A5F] bg-[#1E3A5F]/5 shadow-sm"
                            : "border-gray-200 hover:border-gray-300 bg-white"
                        }`}
                      >
                        <div
                          className="w-10 h-10 rounded-lg flex items-center justify-center mb-2"
                          style={{ backgroundColor: `${c.color}15` }}
                        >
                          <c.icon className="w-5 h-5" style={{ color: c.color }} />
                        </div>
                        <div className="font-semibold text-sm text-[#1E3A5F]">{c.label}</div>
                        <div className="text-xs text-gray-500 mt-1 hidden md:block">{c.desc}</div>
                      </button>
                    );
                  })}
                </div>

                <form
                  onSubmit={handleSubmit}
                  className="bg-white border border-gray-100 rounded-3xl shadow-sm p-8 sm:p-10 space-y-6"
                  data-testid="contact-form"
                >
                  <div className="grid sm:grid-cols-2 gap-5">
                    <Field label="Full Name" required>
                      <input
                        data-testid="contact-full-name"
                        required
                        value={form.full_name}
                        onChange={(e) => update("full_name", e.target.value)}
                        className={inputCls}
                        placeholder="Your full name"
                      />
                    </Field>
                    <Field label="Email" required>
                      <input
                        data-testid="contact-email"
                        required
                        type="email"
                        value={form.email}
                        onChange={(e) => update("email", e.target.value)}
                        className={inputCls}
                        placeholder="you@company.com"
                      />
                    </Field>
                    <Field label="Phone">
                      <input
                        data-testid="contact-phone"
                        value={form.phone}
                        onChange={(e) => update("phone", e.target.value)}
                        className={inputCls}
                        placeholder="Optional"
                      />
                    </Field>
                    <Field label="Organisation">
                      <input
                        data-testid="contact-org"
                        value={form.organization}
                        onChange={(e) => update("organization", e.target.value)}
                        className={inputCls}
                        placeholder="Optional"
                      />
                    </Field>
                  </div>

                  <Field label="Subject" required>
                    <input
                      data-testid="contact-subject"
                      required
                      value={form.subject}
                      onChange={(e) => update("subject", e.target.value)}
                      className={inputCls}
                      placeholder="Brief summary of your message"
                    />
                  </Field>

                  <Field label="Message" required>
                    <textarea
                      data-testid="contact-message"
                      required
                      value={form.message}
                      onChange={(e) => update("message", e.target.value)}
                      className={`${inputCls} min-h-[140px] resize-y`}
                      placeholder="Tell us more..."
                    />
                  </Field>

                  {error && (
                    <div
                      className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm"
                      data-testid="contact-error"
                    >
                      {error}
                    </div>
                  )}

                  <Button
                    type="submit"
                    disabled={submitting}
                    data-testid="contact-submit-btn"
                    className="w-full bg-[#1E3A5F] hover:bg-[#152a45] text-white py-6 text-lg rounded-xl"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" /> Sending...
                      </>
                    ) : (
                      <>
                        Send Message <Send className="w-5 h-5 ml-2" />
                      </>
                    )}
                  </Button>
                </form>
              </>
            )}
          </div>
        </div>
      </section>
    </PublicLayout>
  );
};

const inputCls =
  "w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:border-[#FDB813] focus:ring-2 focus:ring-[#FDB813]/20 text-[#1E3A5F] placeholder:text-gray-400 bg-white";

const Field = ({ label, required, children }) => (
  <div>
    <label className="block text-sm font-medium text-[#1E3A5F] mb-2">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    {children}
  </div>
);

const InfoCard = ({ icon: Icon, title, value, href, sub }) => {
  const content = (
    <div className="flex items-start gap-4">
      <div className="w-12 h-12 bg-[#FEF3C7] rounded-xl flex items-center justify-center flex-shrink-0">
        <Icon className="w-6 h-6 text-[#FDB813]" />
      </div>
      <div>
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">{title}</p>
        <p className="text-base font-semibold text-[#1E3A5F]">{value}</p>
        {sub && <p className="text-xs text-gray-500 mt-0.5">{sub}</p>}
      </div>
    </div>
  );
  return href ? (
    <a href={href} className="hover:bg-gray-50 p-3 -m-3 rounded-xl transition-colors block">
      {content}
    </a>
  ) : (
    <div className="p-3">{content}</div>
  );
};

export default ContactPage;
